<template>
  <div class="popup">
    <div
      class="content"
      :class="{
        'is-wide': isWide,
      }"
    >
      <div
        v-if="!hasAddress"
        class="is-bold"
      >
        <v-icon
          v-if="data.icon"
          :color="data.iconColor || null"
        >
          {{ data.icon }}
        </v-icon>
        <div>
          {{ data.name }}
        </div>
      </div>

      <div
        v-if="hasAddress"
        class="title"
      >
        <strong>{{ addressTitle }}</strong>
      </div>
      <div v-if="from">
        {{ isToDifferent ? 'From' : 'At' }}: {{ from }}
      </div>
      <div v-if="to && isToDifferent">
        To: {{ to }}
      </div>
      <div v-if="data.eta">
        ETA: {{ data.eta }}
      </div>
      <div
        v-if="hasAddress"
        class="address-container"
      >
        <div class="icon-container">
          <v-icon
            color="grey"
          >
            mdi-map-marker
          </v-icon>
        </div>
        <div>
          <div v-if="address.road">
            {{ address.road }}
          </div>
          <div v-if="address.city">
            {{ address.city }}
          </div>
          <div v-if="address.county && (!address.city || address.city !== address.county)">
            {{ address.county }}
          </div>
          <div v-if="address.state">
            {{ address.state }}
          </div>
          <div v-if="address.country">
            {{ address.country }}
          </div>
        </div>
      </div>
      <div v-if="hasAccuracy">
        <div>Accuracy: {{ accuracy.toLocaleString() }} {{ metresLabel }}</div>
      </div>
      <div
        v-if="hasCoordinate"
        class="coordinate-container"
      >
        <ExternalLink
          :name="`${coordinate.latitude}, ${coordinate.longitude}`"
          :href="`https://www.google.com/maps/search/?api=1&query=${coordinate.latitude},${coordinate.longitude}`"
          tooltip-text="View on Google Maps"
        />
      </div>
      <div
        v-for="(item, i) in dataToDisplay"
        :key="`item-${i}`"
        class="key-value-data"
      >
        {{ item.name }}: {{ item.value }}
      </div>
    </div>
  </div>
</template>
<script>
import { constructDate, constructTime } from '../../../helpers/date-formatting-helper';
import constructDisplayAddress from '../../../helpers/nb-iot-current-location-helper';
import ExternalLink from '../links/external-link';

export default {
  name: 'MapPopup',
  components: {
    ExternalLink,
  },
  props: {
    data: { type: Object, default: null },
    isWide: { type: Boolean, default: false },
    showAllData: { type: Boolean, default: false },
  },
  computed: {
    hasAddress() {
      return this.data && this.data.address;
    },
    address() {
      return this.hasAddress ? JSON.parse(this.data.address) : null;
    },
    hasCoordinate() {
      return this.data && this.data.coordinate;
    },
    coordinate() {
      if (!this.hasCoordinate) {
        return null;
      }
      const coordinate = JSON.parse(this.data.coordinate);
      return {
        latitude: coordinate.latitude.toFixed(4),
        longitude: coordinate.longitude.toFixed(4),
      };
    },
    hasAccuracy() {
      return this.data && this.data.accuracy;
    },
    accuracy() {
      return this.hasAccuracy ? JSON.parse(this.data.accuracy) : null;
    },
    metresLabel() {
      return navigator.language.startsWith('en-US') ? 'meters' : 'metres';
    },
    from() {
      if (!this.data || !this.data.timestamp) {
        return null;
      }

      const date = new Date(this.data.timestamp);
      return `${constructDate(date)}, ${constructTime(date)}`;
    },
    to() {
      if (!this.data || !this.data.lastUpdatedAt) {
        return null;
      }

      const date = new Date(this.data.lastUpdatedAt);
      return `${constructDate(date)}, ${constructTime(date)}`;
    },
    isToDifferent() {
      if (!this.data || !this.data.timestamp || !this.data.lastUpdatedAt) {
        return false;
      }

      return this.data.timestamp !== this.data.lastUpdatedAt;
    },
    addressTitle() {
      if (!this.hasAddress) {
        return null;
      }

      return constructDisplayAddress(this.address);
    },
    dataToDisplay() {
      return Object.values(this.data)
        .filter((d) => {
          try {
            const item = JSON.parse(d);
            if (item.name) {
              return item;
            }
          } catch {
            // ignore
          }

          return false;
        }).map((d) => JSON.parse(d));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.popup {
  color: $black;
  font-family: Helvetica-Neue;
  text-align: center;
  padding: 0px 10px;
  .content {
    width: 110px;
    display: flex;
    flex-flow: column;
    justify-content: center;
    align-items: center;
    > div {
      width: 100%;
      display: flex;
      flex-flow: row;
      justify-content: space-evenly;
      align-items: center;
      margin-bottom: 5px;
    }
    &.is-wide {
      min-width: 200px;
    }
    .title {
      font-size: 0.875rem;
      margin-bottom: 5px;
    }
    .address-container {
      display: flex;
      flex-flow: row;
      justify-content: center;
      align-items: flex-start;
    }
    .icon-container {
      width: 20px;
      margin-top: 5px;
      margin-right: 10px;
    }
    .coordinate-container {
      font-size: 1rem;
      color: $tealBright;
      display: flex;
      flex-flow: row;
      justify-content: center;
      align-items: center;
    }
    .key-value-data {
      padding-left: 10px;
      text-align: left;
      justify-content: flex-start;
    }
    .is-bold {
      font-weight: bold;
      font-size: 0.875rem;
    }
  }
}
</style>
